/**
 * Rail control to add, edit, and delete composite widgets for the open tab.
 */

import React, { useMemo, useState } from 'react';
import {
  COMPOSITE_KINDS,
  type CatalogKpi,
  type CompositeCard,
  type CompositeDef,
  type CompositeKind
} from './types';

const MAX_COMPOSITES = 12;
const MIN_MEMBERS = 2;
const MAX_MEMBERS = 8;

interface CompositeEditorProps {
  tab: string;
  catalog: CatalogKpi[];
  cards: CompositeCard[];
  saving: boolean;
  onSave: (defs: CompositeDef[]) => void;
  t: (key: string) => string;
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `cmp-${crypto.randomUUID()}`;
  }
  return `cmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function mixKey(item: CatalogKpi): string {
  return `${String(item.type || '').toUpperCase()}|${String(item.unit || '').toLowerCase()}`;
}

function kindLabel(kind: CompositeKind, t: (key: string) => string): string {
  const keys: Record<CompositeKind, string> = {
    donut: 'kpi.compositeKindDonut',
    'stack-column': 'kpi.compositeKindStackCol',
    'stack-row': 'kpi.compositeKindStackRow',
    'cluster-column': 'kpi.compositeKindClusterCol',
    'cluster-row': 'kpi.compositeKindClusterRow'
  };
  const fallback: Record<CompositeKind, string> = {
    donut: 'Donut (plan inner, actual outer)',
    'stack-column': 'Stacked column',
    'stack-row': 'Stacked row',
    'cluster-column': 'Clustered column',
    'cluster-row': 'Clustered row'
  };
  return t(keys[kind]) || fallback[kind];
}

function cardsToDefs(tab: string, cards: CompositeCard[]): CompositeDef[] {
  return cards.map((card) => ({
    id: card.id,
    tab,
    name: card.name,
    kind: card.kind,
    memberKpiIds: card.memberKpiIds?.length
      ? card.memberKpiIds
      : card.slices.map((slice) => slice.kpiId).filter(Boolean),
    denominatorKpiId: card.denominatorKpiId ?? null,
    hidden: card.hidden === true
  }));
}

export const CompositeEditor: React.FC<CompositeEditorProps> = ({
  tab,
  catalog,
  cards,
  saving,
  onSave,
  t
}) => {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<CompositeKind>('donut');
  const [members, setMembers] = useState<string[]>([]);
  const [denominator, setDenominator] = useState('');
  const [formError, setFormError] = useState('');

  const byId = useMemo(() => new Map(catalog.map((item) => [item.kpiId, item])), [catalog]);
  const selectedItems = members.map((id) => byId.get(id)).filter((item): item is CatalogKpi => !!item);
  const allowedKey = selectedItems.length > 0 ? mixKey(selectedItems[0]) : null;
  const memberSet = new Set(members);

  const startAdd = () => {
    setEditId(null);
    setName('');
    setKind('donut');
    setMembers([]);
    setDenominator('');
    setFormError('');
    setOpen(true);
  };

  const startEdit = (card: CompositeCard) => {
    setEditId(card.id);
    setName(card.name);
    setKind(card.kind);
    setMembers(
      card.memberKpiIds?.length
        ? card.memberKpiIds
        : card.slices.map((slice) => slice.kpiId).filter(Boolean)
    );
    setDenominator(card.denominatorKpiId || '');
    setFormError('');
    setOpen(true);
  };

  const toggleMember = (kpiId: string) => {
    setMembers((current) => {
      if (current.includes(kpiId)) return current.filter((id) => id !== kpiId);
      if (current.length >= MAX_MEMBERS) return current;
      return [...current, kpiId];
    });
  };

  const persist = (nextCards: CompositeCard[], override?: CompositeDef) => {
    const defs = cardsToDefs(tab, nextCards);
    if (!override) {
      onSave(defs);
      return;
    }
    onSave([...defs.filter((item) => item.id !== override.id), override]);
  };

  const saveForm = () => {
    if (members.length < MIN_MEMBERS) {
      setFormError(t('kpi.compositeNeedTwo') || 'Pick at least two KPIs.');
      return;
    }
    if (allowedKey && selectedItems.some((item) => mixKey(item) !== allowedKey)) {
      setFormError(t('kpi.compositeSameType') || 'Members must share the same type and unit.');
      return;
    }
    if (!editId && cards.length >= MAX_COMPOSITES) {
      setFormError(t('kpi.compositeMax') || 'You can have at most 12 composites.');
      return;
    }
    persist(cards, {
      id: editId || newId(),
      tab,
      name: name.trim().slice(0, 60) || t('kpi.compositeSection') || 'Composite',
      kind,
      memberKpiIds: members,
      denominatorKpiId: denominator && !members.includes(denominator) ? denominator : null,
      hidden: false
    });
    setOpen(false);
  };

  const remove = (id: string) => {
    persist(cards.filter((card) => card.id !== id));
  };

  return (
    <div className="mb-3 pb-2 border-b border-[var(--color-border)]">
      <div className="flex items-center justify-between gap-1 mb-1">
        <h3 className="text-xs font-medium text-[var(--color-surface-text)]">
          {t('kpi.compositeSection') || 'Composites'}
        </h3>
        <button
          type="button"
          disabled={saving || (!open && cards.length >= MAX_COMPOSITES)}
          className="text-[10px] text-[var(--color-secondary-text)] hover:underline disabled:opacity-50"
          onClick={open ? () => setOpen(false) : startAdd}
        >
          {open ? t('kpi.compositeCancel') || 'Cancel' : t('kpi.compositeAdd') || 'Add composite'}
        </button>
      </div>
      <p className="text-[10px] text-[var(--color-secondary-text)] mb-1 leading-tight">
        {t('kpi.compositeHint') || 'Charts of existing KPIs. Not catalog rows.'}
      </p>

      {cards.length > 0 && (
        <ul className="space-y-0.5 mb-1">
          {cards.map((card) => (
            <li key={card.id} className="flex items-center justify-between gap-1 text-[10px]">
              <label className="flex items-center gap-1 min-w-0 text-[var(--color-surface-text)]">
                <input
                  type="checkbox"
                  className="mt-0"
                  checked={card.hidden !== true}
                  disabled={saving}
                  onChange={() =>
                    persist(
                      cards.map((item) =>
                        item.id === card.id ? { ...item, hidden: item.hidden !== true } : item
                      )
                    )
                  }
                />
                <span className="truncate" title={card.name}>
                  {card.name}
                </span>
              </label>
              <span className="shrink-0 flex gap-1">
                <button
                  type="button"
                  disabled={saving}
                  className="text-[var(--color-secondary-text)] hover:underline"
                  onClick={() => startEdit(card)}
                >
                  {t('kpi.compositeEdit') || 'Edit'}
                </button>
                <button
                  type="button"
                  disabled={saving}
                  className="text-[var(--color-secondary-text)] hover:underline"
                  onClick={() => remove(card.id)}
                >
                  {t('kpi.compositeDelete') || 'Delete'}
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {open && (
        <div className="space-y-1.5">
          <label className="block">
            <span className="block text-[10px] text-[var(--color-secondary-text)] mb-0.5">
              {t('kpi.compositeName') || 'Name'}
            </span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="w-full text-[11px] px-1.5 py-0.5 border border-[var(--color-border)] rounded bg-[var(--color-surface)] text-[var(--color-surface-text)]"
              maxLength={60}
            />
          </label>
          <label className="block">
            <span className="block text-[10px] text-[var(--color-secondary-text)] mb-0.5">
              {t('kpi.compositeKind') || 'Chart'}
            </span>
            <select
              value={kind}
              onChange={(event) => setKind(event.target.value as CompositeKind)}
              className="w-full text-[11px] px-1.5 py-0.5 border border-[var(--color-border)] rounded bg-[var(--color-surface)] text-[var(--color-surface-text)]"
            >
              {COMPOSITE_KINDS.map((item) => (
                <option key={item} value={item}>
                  {kindLabel(item, t)}
                </option>
              ))}
            </select>
          </label>
          <fieldset>
            <legend className="text-[10px] text-[var(--color-secondary-text)] mb-0.5">
              {t('kpi.compositeMembers') || 'Members'}
            </legend>
            <ul className="max-h-28 overflow-y-auto space-y-0.5">
              {catalog.map((item) => {
                const blocked = allowedKey !== null && mixKey(item) !== allowedKey && !memberSet.has(item.kpiId);
                return (
                  <li key={item.kpiId}>
                    <label className="flex items-start gap-1 text-[10px] text-[var(--color-surface-text)]">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={memberSet.has(item.kpiId)}
                        disabled={blocked || saving}
                        onChange={() => toggleMember(item.kpiId)}
                      />
                      <span className="leading-tight">{item.kpiName}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
          <label className="block">
            <span className="block text-[10px] text-[var(--color-secondary-text)] mb-0.5">
              {t('kpi.compositeTotal') || 'Total (optional)'}
            </span>
            <select
              value={denominator}
              onChange={(event) => setDenominator(event.target.value)}
              className="w-full text-[11px] px-1.5 py-0.5 border border-[var(--color-border)] rounded bg-[var(--color-surface)] text-[var(--color-surface-text)]"
            >
              <option value="">{t('kpi.compositeTotalNone') || 'Sum of members'}</option>
              {catalog
                .filter((item) => !memberSet.has(item.kpiId) && (allowedKey === null || mixKey(item) === allowedKey))
                .map((item) => (
                  <option key={item.kpiId} value={item.kpiId}>
                    {item.kpiName}
                  </option>
                ))}
            </select>
          </label>
          {formError && <p className="text-[10px] text-red-700">{formError}</p>}
          <button
            type="button"
            disabled={saving}
            className="text-[10px] px-2 py-0.5 rounded bg-[var(--color-primary-bg)] text-[var(--color-secondary-text)] disabled:opacity-50"
            onClick={saveForm}
          >
            {t('kpi.compositeSave') || 'Save'}
          </button>
        </div>
      )}
    </div>
  );
};
